import {
  APPOINTMENT_MODE_LABELS,
  APPOINTMENT_PLATFORM_LABELS,
  AppointmentMode,
  AppointmentPlatform,
} from '@performance-crm/shared';
import { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useAdvisors, useProjects } from '@/lib/queries';
import { defaultAppointmentLocation, type AppointmentForm } from './appointment';

/** Modalidad de la cita: presencial (lugar) o virtual (plataforma y enlace). */
export function AppointmentFields({
  value,
  onChange,
  projectId,
  companyId,
  idPrefix = 'appt',
}: {
  value: AppointmentForm;
  onChange: (v: AppointmentForm) => void;
  /** Proyecto del lead: sugiere la sala de ventas como lugar. */
  projectId?: string;
  /** Empresa del lead: habilita elegir el asesor de sala (7.22). */
  companyId?: string;
  idPrefix?: string;
}) {
  const { data: projects } = useProjects();
  const { data: advisors } = useAdvisors(companyId);
  const project = projects?.find((p) => p.id === projectId);
  const suggested = defaultAppointmentLocation(project);
  const set = (patch: Partial<AppointmentForm>) => onChange({ ...value, ...patch });
  // Al abrir con un proyecto conocido y sin lugar escrito, se sugiere la sala de ventas.
  useEffect(() => {
    if (suggested && !value.location) onChange({ ...value, location: suggested });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggested]);
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="appointment-fields">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-mode`}>Modalidad</Label>
        <Select
          id={`${idPrefix}-mode`}
          value={value.mode}
          onChange={(e) => set({ mode: e.target.value as AppointmentMode })}
        >
          {Object.values(AppointmentMode).map((m) => (
            <option key={m} value={m}>
              {APPOINTMENT_MODE_LABELS[m]}
            </option>
          ))}
        </Select>
      </div>
      {value.mode === 'VIRTUAL' ? (
        <>
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-platform`}>Plataforma</Label>
            <Select
              id={`${idPrefix}-platform`}
              value={value.platform}
              onChange={(e) => set({ platform: e.target.value as AppointmentPlatform })}
              required
            >
              <option value="">Elige…</option>
              {Object.values(AppointmentPlatform).map((p) => (
                <option key={p} value={p}>
                  {APPOINTMENT_PLATFORM_LABELS[p]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor={`${idPrefix}-url`}>Enlace de la reunión (opcional)</Label>
            <Input
              id={`${idPrefix}-url`}
              type="url"
              value={value.url}
              onChange={(e) => set({ url: e.target.value })}
              placeholder="https://meet.google.com/…"
            />
          </div>
        </>
      ) : (
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-location`}>Lugar</Label>
          <Input
            id={`${idPrefix}-location`}
            value={value.location}
            onChange={(e) => set({ location: e.target.value })}
            placeholder={suggested || 'Sala de ventas, oficina, dirección…'}
            maxLength={200}
          />
        </div>
      )}
      {companyId && (
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor={`${idPrefix}-room`}>Asesor de sala (opcional)</Label>
          <Select
            id={`${idPrefix}-room`}
            value={value.roomAdvisorId}
            onChange={(e) => set({ roomAdvisorId: e.target.value })}
            data-testid={`${idPrefix}-room`}
          >
            <option value="">El mismo asesor del lead</option>
            {advisors?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.fullName}
              </option>
            ))}
          </Select>
          <p className="text-muted-foreground text-xs">
            Con otro asesor en sala, la cita concretada la confirman los dos y el lead pasa al
            asesor de sala.
          </p>
        </div>
      )}
    </div>
  );
}
